/**
 * How long a sign-in lasts, named once for the server's better-auth config
 * (src/lib/auth.ts) and the client that renews it
 * (src/components/session-renewal.tsx).
 *
 * A session ends after {@link SESSION_EXPIRES_IN} seconds without a renewal,
 * and it is renewed at most once per {@link SESSION_UPDATE_AGE}: a person who
 * opens the app at least once a month stays signed in. The row and the cookie
 * are renewed together, by the auth route only, because a server component
 * cannot write a cookie (the board item "The session cookie is never renewed,
 * so a daily user signs in again every 7 days", 2026-10-26).
 *
 * Pure data and one pure function: safe from a client component.
 */

/** Seconds a session lives past its last renewal (better-auth `expiresIn`). */
export const SESSION_EXPIRES_IN = 60 * 60 * 24 * 30;

/** Seconds between two renewals of one session (better-auth `updateAge`). */
export const SESSION_UPDATE_AGE = 60 * 60 * 24;

/**
 * The moment (epoch ms) a session that expires at `expiresAt` is due for a
 * renewal: one update age after it was last renewed. better-auth's own test
 * (`shouldBeUpdated` in its get-session endpoint), restated on the client so
 * the client asks only when the server will renew.
 */
export function renewalDueAt(expiresAt: number): number {
  return expiresAt - SESSION_EXPIRES_IN * 1000 + SESSION_UPDATE_AGE * 1000;
}

/**
 * The client's renewal request: a POST to better-auth's `/get-session`, which
 * renews when the session is due and otherwise only answers.
 * `disableCookieCache` makes the route read the row (the 60-second cookie
 * cache answers without one), and the route takes a POST only with a JSON
 * body: better-call answers 415 to a body without that content type, and a
 * browser's POST with no body still reaches it with an empty one.
 */
export const SESSION_RENEWAL_URL = '/api/auth/get-session?disableCookieCache=true';
export const SESSION_RENEWAL_INIT = {
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: '{}',
} as const satisfies RequestInit;
